import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertTriangle } from 'lucide-react';
import websocketService from '../utils/websocket';
import ConfirmationPopup from './ConfirmationPopup';

/**
 * Global component to handle WebSocket session events
 * such as force logout from another device or session expiration.
 */
const WebSocketSessionHandler = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showPopup, setShowPopup] = useState(false);
    const [popupContent, setPopupContent] = useState({
        title: "Session Conflict",
        message: "Your session has been terminated because you logged in from another device.",
        isForceLogout: false
    });

    useEffect(() => {
        // Handle auth error events from WebSocket
        const handleAuthError = (data) => {
            console.log("WebSocketSessionHandler: Auth error received:", data);
            
            const isForceLogout = data.isForceLogout || 
                               (data.message && (
                                   data.message.toLowerCase().includes("logged in from another device") || 
                                   data.message.toLowerCase().includes("duplicate login") ||
                                   data.message.toLowerCase().includes("session terminated") ||
                                   data.message.toLowerCase().includes("kicked")
                               ));

            if (isForceLogout) {
                setPopupContent({
                    title: "Session Conflict",
                    message: data.message || "Your session has been terminated because you logged in from another device.",
                    isForceLogout: true
                });
                setShowPopup(true);
            } else if (data.message && (data.message.includes("Session expired") || data.message.includes("401"))) {
                setPopupContent({
                    title: "Session Expired",
                    message: "Your session has expired. Please login again.",
                    isForceLogout: false
                });
                setShowPopup(true);
            }
        };

        // Handle token expired event from WebSocket utility
        const handleTokenExpired = (data) => {
            console.log("WebSocketSessionHandler: Token expired received:", data);
            setPopupContent({
                title: "Session Expired",
                message: data.message || "Your session has expired. Please login again.",
                isForceLogout: false
            });
            setShowPopup(true);
        };

        // Handle session expired from networking.js
        const handleNetworkingSessionExpired = (event) => {
            console.log("WebSocketSessionHandler: Networking session expired:", event.detail);
            setPopupContent({
                title: "Session Expired",
                message: event.detail.message || "Your session has expired. Please login again.",
                isForceLogout: false
            });
            setShowPopup(true);
        };

        // Handle logout confirmation (403) from networking.js
        const handleLogoutConfirmation = (event) => {
            console.log("WebSocketSessionHandler: Logout confirmation needed:", event.detail);
            setPopupContent({
                title: "Session Conflict",
                message: event.detail.message || "Your session has been terminated because you logged in from another device.",
                isForceLogout: true
            });
            setShowPopup(true);
        };

        // Register handlers
        websocketService.on("auth_error", handleAuthError);
        websocketService.on("token_expired", handleTokenExpired);
        window.addEventListener('session_expired', handleNetworkingSessionExpired);
        window.addEventListener('logout_confirmation_needed', handleLogoutConfirmation);

        // Also check if the user is already logged in and try to connect WebSocket if not connected
        const token = localStorage.getItem('authToken');
        const userJson = localStorage.getItem('user');
        
        if (token && userJson) {
            const user = JSON.parse(userJson);
            const status = websocketService.getConnectionStatus();
            if (!status.isConnected && !status.intentionalDisconnect) {
                console.log("WebSocketSessionHandler: Reconnecting WebSocket for user:", user.id);
                websocketService.connect(token, user.id).catch(err => {
                    console.error("WebSocketSessionHandler: Reconnection failed:", err);
                });
            }
        }

        return () => {
            websocketService.off("auth_error", handleAuthError);
            websocketService.off("token_expired", handleTokenExpired);
            window.removeEventListener('session_expired', handleNetworkingSessionExpired);
            window.removeEventListener('logout_confirmation_needed', handleLogoutConfirmation);
        };
    }, []);

    const handleConfirm = () => {
        setShowPopup(false);
        
        // Clear session data
        localStorage.clear();
        sessionStorage.clear();
        
        // Disconnect WebSocket
        websocketService.disconnect();
        
        // Redirect to login page
        navigate('/login', { 
            state: { 
                from: location.pathname,
                message: popupContent.message,
                isForcedLogout: popupContent.isForceLogout
            } 
        });
        
        // Optional: reload the page to ensure all states are cleared
        // window.location.reload();
    };

    if (!showPopup) return null;

    return (
        <ConfirmationPopup
            isOpen={showPopup}
            onClose={handleConfirm}
            onConfirm={handleConfirm}
            title={popupContent.title}
            message={popupContent.message}
            confirmText="Login Again"
            cancelText="Cancel"
        />
    );
};

export default WebSocketSessionHandler;
